import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, MapPin, FileText, Download } from 'lucide-react';
import { projectService } from '../services/projectService';
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';
import { Button } from '@/components/ui/Button';
import { downloadFile } from '@/lib/API';

const displayUserName = (u) => {
  if (!u || typeof u !== 'object') return '';
  if (u.name && String(u.name).trim()) return u.name;
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return n || '';
};

const formatCurrencyIn = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN')}`;
};

const formatTower = (p) => {
  const type = p?.towerType;
  const h = p?.height;
  if (type && h != null && h !== '' && Number.isFinite(Number(h))) {
    return `${type} · ${h} m`;
  }
  if (type) return type;
  if (h != null && h !== '' && Number.isFinite(Number(h))) return `${h} m`;
  return '—';
};

const fmt = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const DetailCard = ({ label, value }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 min-w-0">
    <div className="text-[13px] font-medium text-slate-500 tracking-wide mb-0.5 tracking-tighter">{label}</div>
    <div className="text-[12px] font-medium text-slate-700 break-words">{value || '—'}</div>
  </div>
);

export const ViewProjectModal = ({ projectId, onClose, onProjectUpdated }) => {
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadError('');
      try {
        const data = await projectService.getProjectById(projectId);
        if (!cancelled) setProject(data);
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.message || e.message || 'Failed to load project');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const timeline = useMemo(() => {
    if (!project) return [];
    const log = Array.isArray(project.activityLog) ? [...project.activityLog] : [];
    const hasCreated = log.some((e) => e.type === 'created');
    if (!hasCreated && project.createdAt) {
      log.unshift({
        type: 'created',
        message: 'Project created',
        at: project.createdAt
      });
    }
    return log.sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [project]);

  const handleAddNote = async () => {
    const text = noteDraft.trim();
    if (!text || !project?._id) return;
    setSavingNote(true);
    try {
      const updated = await projectService.addProjectActivityNote(project._id, text);
      setProject(updated);
      setNoteDraft('');
      onProjectUpdated?.();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingNote(false);
    }
  };

  const onDownload = (e, doc) => {
    e.preventDefault();
    // Use the secure streaming endpoint for guaranteed high-fidelity downloads
    const streamUrl = `/projects/documents/stream?filePath=${encodeURIComponent(doc.path)}`;
    downloadFile(streamUrl, doc.originalName);
  };

  if (!projectId) return null;
  
  return createPortal(
    <div className="fixed inset-0 z-[50] flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" aria-hidden />

      <div className="relative z-[51] w-full max-w-5xl max-h-[92vh] bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 shrink-0 bg-white">
          <h2 className="text-base font-semibold text-slate-800 truncate pr-2">Project View</h2>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 border-b lg:border-b-0 lg:border-r border-slate-100 min-w-0">
            {loadError && (
              <p className="text-sm font-bold text-rose-600 mb-4">{loadError}</p>
            )}
            {!project && !loadError && (
              <div className="py-12 text-center text-slate-400 text-xs font-bold">Loading…</div>
            )}
            {project && (
              <>
                <div className="flex flex-col sm:flex-row gap-4 items-start mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#2b448f] text-white flex items-center justify-center text-lg font-extrabold shrink-0">
                    {(project.customerName || 'P').charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                  <div className='flex gap-2 items-center'>
                      <h3 className="text-[17px] font-bold text-slate-800 truncate">{project.projectName}</h3>
                    <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">{project.status}</span>
                  </div>
                    <p className="text-[10px] font-medium text-blue-500 px-2 py-0.5 rounded-md border border-blue-100 bg-blue-50/50 uppercase tracking-tight inline-block mt-1">{project.projectId} </p>
                   <div className='flex items-center gap-2'> <p className="text-[12px] text-slate-500 mt-1">{project.customerName}</p>
                    <div className="flex items-start gap-1.5 text-[12px] text-slate-500 mt-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        {[
                          project.address?.line1,
                          project.address?.line2,
                          project.address?.city,
                          project.address?.state,
                          project.address?.country
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div></div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  <div className="sm:col-span-2 mb-1">
                    <h4 className="text-[11px] font-bold text-[#2b448f] uppercase tracking-wider border-b border-slate-100 pb-1.5">General & Lead Information</h4>
                  </div>
                  <DetailCard label="Client Name" value={project.customerName || displayUserName(project.leadId)} />
                  <DetailCard label="Project ID (Verve ID)" value={project.verveId || project.projectId || project.customId} />
                  <DetailCard label="Phone Number" value={project.phoneNumber || project.leadId?.phoneNumber} />
                  <DetailCard label="Email" value={project.email || project.leadId?.email} />
                  <DetailCard label="Project Status" value={project.status || project.projectStatus} />
                  <DetailCard label="Requirement Type" value={project.requirementType || project.leadId?.requirementType} />
                  <DetailCard label="Property Type" value={project.propertyType || project.leadId?.propertyType} />
                  <DetailCard label="Source" value={project.source || project.leadId?.source} />
                  <DetailCard label="Reference Details" value={project.referenceDetails || project.referenceType || project.leadId?.referenceDetails || project.leadId?.referenceType} />
                  <DetailCard label="Project Created Date" value={fmtDate(project.createdAt)} />
                  <DetailCard label="Start Date" value={fmtDate(project.startDate || project.createdAt)} />
                  <DetailCard label="Expected End Date" value={fmtDate(project.expectedEndDate)} />

                  <div className="sm:col-span-2 mt-3 mb-1">
                    <h4 className="text-[11px] font-bold text-[#2b448f] uppercase tracking-wider border-b border-slate-100 pb-1.5">Site & Survey Details (Lead Stage)</h4>
                  </div>
                  <DetailCard label="Location / City" value={project.location || project.city || project.leadId?.city || project.leadId?.location} />
                  <DetailCard label="District & State" value={[project.district || project.leadId?.district, project.state || project.leadId?.state].filter(Boolean).join(', ') || '—'} />
                  <DetailCard label="Full Address" value={[project.addressLine1 || project.leadId?.addressLine1 || project.address?.line1, project.addressLine2 || project.leadId?.addressLine2 || project.address?.line2, project.pincode || project.leadId?.pincode].filter(Boolean).join(', ') || '—'} />
                  <DetailCard label="Meeting Date" value={fmtDate(project.meetingDate || project.appointmentDate || project.leadId?.meetingDate || project.leadId?.appointmentDate)} />
                  <DetailCard label="Attended By" value={project.attendedBy || project.leadId?.attendedBy} />
                  <DetailCard label="Floor Details" value={project.floorDetails || project.leadId?.floorDetails} />
                  <DetailCard label="Power Consumption" value={project.existingPowerConsumption || project.leadId?.existingPowerConsumption} />
                  <DetailCard label="Requested Capacity" value={project.verveCapacityRequested || project.leadId?.verveCapacityRequested} />
                  <div className="sm:col-span-2">
                    <DetailCard label="Survey / Inspection Notes" value={project.inspectionNote || project.leadId?.inspectionNote} />
                  </div>
                  <div className="sm:col-span-2">
                    <DetailCard label="General Remarks / Notes" value={project.notes || project.lastNote || project.leadId?.notes} />
                  </div>

                  <div className="sm:col-span-2 mt-3 mb-1">
                    <h4 className="text-[11px] font-bold text-[#2b448f] uppercase tracking-wider border-b border-slate-100 pb-1.5">Financial & Budget Summary</h4>
                  </div>
                  <DetailCard label="Project Value (₹)" value={formatCurrencyIn(project.projectValue || project.contractValue || 0)} />
                  <DetailCard label="Initial Budget (₹)" value={formatCurrencyIn(project.budget || project.leadId?.budget || 0)} />
                  <DetailCard label="Advance Paid (₹)" value={formatCurrencyIn(project.advancePaid || 0)} />
                  <DetailCard label="Total Received (₹)" value={formatCurrencyIn(project.totalReceived || 0)} />
                  <DetailCard label="Total Expenses (₹)" value={formatCurrencyIn(project.totalExpenses || 0)} />
                  <DetailCard label="Material Expenses (₹)" value={formatCurrencyIn(project.totalMaterialCost || project.materialExpenses || 0)} />
                  <DetailCard label="Pending Balance (₹)" value={formatCurrencyIn(project.netBalance ?? ((project.projectValue || 0) - (project.totalReceived || 0)))} />
                  <DetailCard label="Profit / Loss (₹)" value={formatCurrencyIn(project.profitLoss ?? ((project.projectValue || 0) - (project.totalExpenses || 0)))} />

                  <div className="sm:col-span-2 mt-3 mb-1">
                    <h4 className="text-[11px] font-bold text-[#2b448f] uppercase tracking-wider border-b border-slate-100 pb-1.5">Team & Assignment</h4>
                  </div>
                  <DetailCard
                    label="Project Manager"
                    value={
                      typeof project.projectManager === 'object' && project.projectManager
                        ? (() => {
                            if (Array.isArray(project.projectManager)) {
                              return project.projectManager.map(displayUserName).join(', ') || '—';
                            }
                            const label = displayUserName(project.projectManager);
                            const ph = project.projectManager.phone;
                            return label
                              ? [label, ph ? `· ${ph}` : null].filter(Boolean).join(' ')
                              : '—';
                          })()
                        : '—'
                    }
                  />
                  <DetailCard label="Progress" value={`${project.progress ?? 0}%`} />
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 min-w-0 sm:col-span-2">
                    <div className="text-[13px] font-medium text-slate-500 tracking-wide mb-1.5 tracking-tighter">Installation Team</div>
                    {Array.isArray(project.fieldOfficers) && project.fieldOfficers.length > 0 ? (
                      <ul className="list-none space-y-1 m-0 p-0">
                        {project.fieldOfficers.map((m, i) => {
                          const o = m && typeof m === 'object' ? m : null;
                          const name = o ? displayUserName(o) : '';
                          const trade = o ? formatInstallationTrade(o.installationTrade) : '—';
                          const ph = o?.phone;
                          return (
                            <li key={o?._id || i} className="text-[12px] font-medium text-slate-700 break-words">
                              {name || '—'}
                              <span className="text-slate-500"> · {trade}</span>
                              {ph ? <span className="text-slate-400"> · {ph}</span> : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="text-[11px] font-bold text-slate-500">No installation members yet</div>
                    )}
                  </div>

                  {(project.commencementDetails || project.remarks) && (
                    <div className="sm:col-span-2 grid grid-cols-1 gap-3">
                      {project.commencementDetails && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 min-w-0">
                          <div className="text-[13px] font-medium text-slate-500 tracking-wide mb-1.5 tracking-tighter">Commencement Details</div>
                          <div className="text-[12px] font-medium text-slate-700 whitespace-pre-wrap break-words">{project.commencementDetails}</div>
                        </div>
                      )}
                      {project.remarks && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 min-w-0">
                          <div className="text-[13px] font-medium text-slate-500 tracking-wide mb-1.5 tracking-tighter">Remarks</div>
                          <div className="text-[12px] font-medium text-slate-700 whitespace-pre-wrap break-words">{project.remarks}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 min-w-0 sm:col-span-2">
                    <div className="text-[13px] font-medium text-slate-500 tracking-wide mb-1.5 tracking-tighter">Project Documents</div>
                    {Array.isArray(project.mergedDocuments) && project.mergedDocuments.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {project.mergedDocuments
                          .filter(doc => doc?.path && doc?.originalName)
                          .map((doc, idx) => (
                          <button
                            key={`${doc.path}-${idx}`}
                            onClick={(e) => onDownload(e, doc)}
                            className="flex items-center gap-2.5 px-3 py-1.5 bg-white border border-slate-100 rounded-lg hover:border-[#2b448f] hover:bg-slate-50/50 transition-all group min-w-0 max-w-full shadow-sm"
                          >
                            <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors">
                              <FileText className="w-4 h-4 text-slate-400 group-hover:text-[#2b448f]" />
                            </div>
                            <div className="flex flex-col min-w-0 overflow-hidden text-left">
                              <span className="text-[12px] font-medium text-slate-700 truncate group-hover:text-[#2b448f] transition-colors">
                                {doc.originalName}
                              </span>
                              <span className="text-[10px] font-medium text-slate-400 flex items-center gap-1 uppercase tracking-tighter">
                                {doc.source === 'balance_update' ? 'Verification' : 'Creation'} · Download <Download size={8} />
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[12px] font-medium text-slate-500 py-1">No documents uploaded yet</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex-1 flex flex-col min-h-[280px] lg:min-h-0 lg:max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-100 shrink-0">
              <div className="text-[12px] font-medium text-slate-500 mb-2 tracking-tight">Add Note</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add note..."
                  disabled={!project}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-[12px] font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:bg-slate-100"
                />
                <button
                  type="button"
                  title="Save note"
                  onClick={handleAddNote}
                  disabled={!project || !noteDraft.trim() || savingNote}
                  className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-[#2b448f] text-white hover:bg-[#243d7a] transition-all disabled:opacity-40"
                >
                  {savingNote ? (
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-[13px] font-medium text-slate-500 mb-4 tracking-tight">Activity</div>
              <div className="flex flex-col gap-5 border-l border-slate-200 pl-4 ml-1">
                {timeline.map((ev, idx) => (
                  <div key={`${ev.at}-${idx}`} className="relative">
                    <span
                      className={`absolute -left-[20.5px] top-1 w-2 h-2 rounded-full border-4 border-white ${
                        idx === 0 ? 'bg-[#2b448f]' : 'bg-slate-300'
                      }`}
                    />
                    <p className="text-[12px] font-medium text-slate-600 leading-relaxed">
                      {ev.message}
                      {ev.actorName ? <span className="text-slate-500 font-medium"> · {ev.actorName}</span> : null}
                    </p>
                    <p className="text-[10px] font-medium text-slate-400 mt-0.5">{fmt(ev.at)}</p>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="button"
              variant=""
              size="sm"
              onClick={onClose}
              className="p-2.5 m-4 border border-slate-200 text-slate-500 bg-slate-50"
            >
              Close View
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
